import { createPaymentPlanTool, toPaymentPlanConfig, } from '@debbie/common/dist/agents/tools/create-payment-plan.js';
import { lastDanishBankDayOfMonth } from '@debbie/common/dist/economy/helpers/dates/danish-bank-days.js';
import { getEffectiveFeeStructure } from '@debbie/common/dist/economy/helpers/payment-plans/index.js';
import { Interval, PaymentPlanEntryTypes, } from '@debbie/common/dist/economy/payment-plans/index.js';
import { genObjectKey } from '@debbie/common/dist/util/index.js';
import { majorToCents } from '@debbie/common/dist/util/money.js';
import { useCaseConditions } from '@debbie/cortex/dist/case-conditions/index.js';
import { useCaseVouchersByCase } from '@debbie/cortex/dist/case-vouchers/index.js';
import { useCase } from '@debbie/cortex/dist/cases/index.js';
import { useCreditor } from '@debbie/cortex/dist/creditors/index.js';
import { useDocumentsByReference } from '@debbie/cortex/dist/documents/index.js';
import { useFixedDistributionsByCaseId } from '@debbie/cortex/dist/fixed-distributions/index.js';
import { useGeneratePaymentPlan } from '@debbie/cortex/dist/heavy-calculations/index.js';
import { h } from '@debbie/cortex/dist/index.js';
import { useTenantLoadable } from '@debbie/cortex/dist/tenant/index.js';
import moment from 'moment';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { styled } from 'styled-components';
import THEME from '../../theme.js';
import Button from '../Button.js';
import DateInput from '../DateInput.js';
import Errors from '../Errors.js';
import Edit2 from '../Icons/Edit2.js';
import X from '../Icons/X.js';
import Input from '../Input.js';
import Money from '../Money.js';
import MoneyInput from '../MoneyInput.js';
import IntervalInput from '../PaymentPlan/components/IntervalInput.js';
import PillSelector from '../PillSelector.js';
import { EditError, IconButton, firstZodIssue, } from './shared.js';
const intervalLabel = (lang, interval) => {
    switch (interval) {
        case Interval.MONTH:
            return lang.monthly;
        case Interval['TWO-MONTHS']:
            return lang.everyOtherMonth;
        case Interval.QUARTERLY:
            return lang.quarterly;
        case Interval.YEARLY:
            return lang.yearly;
        case Interval['TWO-WEEKS']:
            return lang.everyOtherWeek;
        case Interval.WEEK:
            return lang.weekly;
        case Interval.LASTBANKDAYMONTH:
            return lang.lastBankDayOfMonth;
    }
};
const Primary = styled.div `
  font-size: 1.05em;
  font-weight: 600;
  color: ${THEME.COLOR_PRIMARY};
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 6px;
`;
const Cadence = styled.span `
  color: ${THEME.COLOR_GREY};
  font-weight: 500;
  font-size: 0.85em;
  text-transform: lowercase;
`;
const Secondary = styled.div `
  margin-top: 2px;
  font-size: 0.85em;
  color: ${THEME.COLOR_GREY};
`;
const Detail = styled.div `
  margin-top: 8px;
  font-size: 0.85em;
  color: ${THEME.COLOR_PRIMARY};
`;
const DetailLabel = styled.span `
  color: ${THEME.COLOR_GREY};
  margin-right: 6px;
`;
const ChangeList = styled.ul `
  margin: 4px 0 0 0;
  padding-left: 16px;
  font-size: 0.85em;

  li {
    margin: 2px 0;
    color: ${THEME.COLOR_PRIMARY};
  }
`;
const StatRow = styled.div `
  margin-top: 12px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  border: 1px solid ${THEME.BORDER};
  border-radius: 3px;
  overflow: hidden;
  background: #fff;
`;
const StatCell = styled.div `
  padding: 8px 10px;
  border-right: 1px solid ${THEME.BORDER};
  font-size: 0.85em;

  &:last-child {
    border-right: none;
  }
`;
const StatLabel = styled.div `
  color: ${THEME.COLOR_GREY};
  font-size: 0.75em;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 2px;
`;
const StatValue = styled.div `
  color: ${THEME.COLOR_PRIMARY};
  font-weight: 500;
`;
const StatPlaceholder = styled.div `
  margin-top: 12px;
  padding: 8px 10px;
  font-size: 0.8em;
  color: ${THEME.COLOR_GREY};
  font-style: italic;
`;
const formatDate = (date) => moment(date).format(THEME.DATE_FORMAT);
const isLastBankDayOfMonth = (date) => {
    const lastBankDay = lastDanishBankDayOfMonth(moment(date));
    return (date.getFullYear() === lastBankDay.year() &&
        date.getMonth() === lastBankDay.month() &&
        date.getDate() === lastBankDay.date());
};
const snapToLastBankDay = (date) => {
    const snapped = lastDanishBankDayOfMonth(date);
    const result = snapped.isBefore(moment(), 'day')
        ? lastDanishBankDayOfMonth(date.clone().add(1, 'month'))
        : snapped;
    return result.format('YYYY-MM-DD');
};
const usePlanPreview = (caseId, input) => {
    const tenantLoadable = useTenantLoadable();
    const tenant = h.hasData(tenantLoadable) ? tenantLoadable.data : null;
    const caseLoadable = useCase(caseId);
    const case_ = h.hasData(caseLoadable) ? caseLoadable.data : null;
    const creditorLoadable = useCreditor(case_?.creditorId);
    const creditor = h.hasData(creditorLoadable) ? creditorLoadable.data : null;
    const vouchersLoadable = useCaseVouchersByCase(caseId);
    const vouchers = h.hasData(vouchersLoadable) ? vouchersLoadable.data : null;
    const documentsLoadable = useDocumentsByReference(caseId);
    const documents = h.hasData(documentsLoadable) ? documentsLoadable.data : null;
    const distributionsLoadable = useFixedDistributionsByCaseId(caseId);
    const distributions = h.hasData(distributionsLoadable)
        ? distributionsLoadable.data
        : null;
    const conditionsLoader = useCaseConditions(caseId);
    const conditions = h.hasData(conditionsLoader) ? conditionsLoader.data : null;
    const parsedInput = createPaymentPlanTool.inputSchema.safeParse(input);
    const sourcesReady = tenant != null &&
        creditor != null &&
        vouchers != null &&
        documents != null &&
        distributions != null &&
        conditions != null;
    const config = sourcesReady && parsedInput.success
        ? toPaymentPlanConfig(parsedInput.data, getEffectiveFeeStructure(creditor.paymentPlansConfig, tenant.paymentPlansConfig))
        : null;
    const generatedPlan = useGeneratePaymentPlan({
        tenant,
        creditor,
        economyEngine: case_?.economyEngine ?? null,
        caseVouchers: vouchers,
        documents,
        fixedDistributions: distributions,
        config,
        conditions,
    });
    const sourceError = [
        h.isError(caseLoadable) ? caseLoadable.error.message : null,
        h.isError(creditorLoadable) ? creditorLoadable.error.message : null,
        h.isError(vouchersLoadable) ? vouchersLoadable.error.message : null,
        h.isError(documentsLoadable) ? documentsLoadable.error.message : null,
        h.isError(distributionsLoadable)
            ? distributionsLoadable.error.message
            : null,
        h.isError(conditionsLoader) ? conditionsLoader.error.message : null,
        h.isError(tenantLoadable) ? tenantLoadable.error.message : null,
    ].find((message) => message != null);
    if (sourceError != null) {
        return { status: 'error', message: sourceError };
    }
    if (!sourcesReady) {
        return { status: 'loading' };
    }
    if (!parsedInput.success) {
        return { status: 'error', message: firstZodIssue(parsedInput.error) };
    }
    if (h.isError(generatedPlan)) {
        return { status: 'error', message: generatedPlan.error.message };
    }
    if (h.hasData(generatedPlan)) {
        return { status: 'ready', entries: generatedPlan.data };
    }
    return { status: 'loading' };
};
const summarizeEntries = (entries) => {
    const normal = entries.filter((e) => e.type === PaymentPlanEntryTypes.NORMAL);
    const totalCents = entries.reduce((s, e) => s + e.amount, 0);
    const last = entries[entries.length - 1] ?? null;
    return {
        entryCount: entries.length,
        installmentCount: normal.length,
        perInstallmentCents: normal[0]?.amount ?? null,
        endDate: last?.date ?? null,
        totalCents,
    };
};
const EDITABLE_KEYS = [
    'method',
    'startDate',
    'interval',
    'duration',
    'amount',
    'amountUpfront',
    'dateUpfront',
    'scheduledAmountChanges',
];
const CreatePaymentPlanBody = ({ input, caseId, showValidationErrors = true, onEditsChange }) => {
    const lang = useSelector((s) => s.lang);
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(input);
    const [editError, setEditError] = useState(null);
    const [durationRaw, setDurationRaw] = useState(input.duration != null ? String(input.duration) : '');
    const effective = editing ? draft : input;
    const cadence = intervalLabel(lang.paymentPlans, effective.interval);
    const preview = usePlanPreview(caseId, effective);
    const previewSummary = preview.status === 'ready' ? summarizeEntries(preview.entries) : null;
    const planDateFilter = draft.interval === Interval.LASTBANKDAYMONTH
        ? isLastBankDayOfMonth
        : undefined;
    const snapPlanDate = (date) => draft.interval === Interval.LASTBANKDAYMONTH
        ? snapToLastBankDay(date)
        : date.format('YYYY-MM-DD');
    const cancelEditing = () => {
        setDraft(input);
        setDurationRaw(input.duration != null ? String(input.duration) : '');
        setEditError(null);
        onEditsChange?.({ edits: null, isValid: true });
        setEditing(false);
    };
    const updateDraft = (changes) => {
        if (onEditsChange == null) {
            return;
        }
        const next = { ...draft, ...changes };
        setDraft(next);
        const patch = {};
        for (const key of EDITABLE_KEYS) {
            if ((key === 'duration' && next.method !== 'duration') ||
                ((key === 'amount' || key === 'scheduledAmountChanges') &&
                    next.method !== 'amount')) {
                continue;
            }
            if (genObjectKey(next[key] ?? null) !== genObjectKey(input[key] ?? null)) {
                patch[key] = next[key] === undefined ? null : next[key];
            }
        }
        if (Object.keys(patch).length > 0) {
            patch.method = next.method;
            patch[next.method === 'duration' ? 'duration' : 'amount'] =
                next.method === 'duration' ? next.duration : next.amount;
        }
        const parsed = createPaymentPlanTool.inputSchema.safeParse(next);
        const startDateOk = next.startDate >= moment().format('YYYY-MM-DD');
        setEditError(!parsed.success
            ? firstZodIssue(parsed.error)
            : !startDateOk
                ? lang.agentProposalBodies.startDateInPast
                : null);
        onEditsChange({
            edits: Object.keys(patch).length > 0 ? patch : null,
            isValid: parsed.success && startDateOk,
        });
    };
    const changeMethod = (method) => {
        if (method === draft.method) {
            return;
        }
        if (method === 'amount') {
            updateDraft({
                method,
                amount: draft.amount ??
                    (previewSummary?.perInstallmentCents != null
                        ? previewSummary.perInstallmentCents / 100
                        : 1),
                duration: undefined,
                scheduledAmountChanges: null,
            });
            return;
        }
        const duration = Math.max(1, Math.min(60, draft.duration ?? previewSummary?.installmentCount ?? 1));
        setDurationRaw(String(duration));
        updateDraft({
            method,
            duration,
            amount: undefined,
            scheduledAmountChanges: null,
        });
    };
    return (React.createElement(React.Fragment, null,
        editing ? (React.createElement(EditFields, null,
            React.createElement(EditActions, null,
                React.createElement(IconButton, { onClick: cancelEditing, title: lang.misc.cancel },
                    React.createElement(X, { size: "16px" }))),
            React.createElement(PillSelector, { value: draft.method, onChange: changeMethod, options: [
                    {
                        key: 'amount',
                        value: 'amount',
                        label: lang.paymentPlans.byAmount,
                    },
                    {
                        key: 'duration',
                        value: 'duration',
                        label: lang.paymentPlans.byDuration,
                    },
                ] }),
            draft.method === 'duration' ? (React.createElement(Input, { placeholder: lang.agentProposalBodies.numberOfInstallments, value: durationRaw, onChange: (e) => {
                    const raw = e.target.value;
                    setDurationRaw(raw);
                    const n = parseInt(raw, 10);
                    updateDraft({
                        duration: raw.trim() === '' || Number.isNaN(n) ? undefined : n,
                    });
                }, style: { marginBottom: '0' } })) : (React.createElement(MoneyInput, { allowNull: true, placeholder: lang.agentProposalBodies.installmentAmount, value: draft.amount != null ? majorToCents(draft.amount) : null, onChange: (cents) => updateDraft({
                    amount: cents == null ? undefined : cents / 100,
                }), style: { marginBottom: '0' } })),
            React.createElement(DateInput, { allowNull: false, placeholder: lang.agentProposalBodies.startDate, value: moment(draft.startDate), minDate: new Date(), filterDate: planDateFilter, onChange: (d) => updateDraft({ startDate: snapPlanDate(moment(d)) }), style: { marginBottom: '0' } }),
            React.createElement(IntervalInput, { interval: draft.interval, onChange: (interval) => updateDraft(interval === Interval.LASTBANKDAYMONTH
                    ? {
                        interval,
                        startDate: snapToLastBankDay(moment(draft.startDate)),
                    }
                    : { interval }) }),
            React.createElement(UpfrontRow, null,
                React.createElement(Field, null,
                    React.createElement(MoneyInput, { allowNull: true, placeholder: lang.paymentPlans.upfrontPayment, value: draft.amountUpfront != null
                            ? majorToCents(draft.amountUpfront)
                            : null, onChange: (cents) => updateDraft(cents == null
                            ? { amountUpfront: null, dateUpfront: null }
                            : { amountUpfront: cents / 100 }), style: { marginBottom: '0' } })),
                React.createElement(Field, null,
                    React.createElement(DateInput, { allowNull: true, placeholder: lang.agentProposalBodies.upfrontDate, value: draft.dateUpfront ? moment(draft.dateUpfront) : null, minDate: new Date(), disabled: draft.amountUpfront == null, onChange: (d) => updateDraft({
                            dateUpfront: d == null ? null : moment(d).format('YYYY-MM-DD'),
                        }), style: { marginBottom: '0' } }))),
            draft.method === 'amount' && (React.createElement(Detail, null,
                React.createElement(DetailLabel, null,
                    lang.agentProposalBodies.scheduledChanges,
                    ":"),
                (draft.scheduledAmountChanges ?? []).map((change, i) => {
                    const rows = draft.scheduledAmountChanges ?? [];
                    const setRows = (next) => updateDraft({
                        scheduledAmountChanges: next.length === 0 ? null : next,
                    });
                    return (React.createElement(ChangeRow, { key: i },
                        React.createElement(MoneyInput, { placeholder: lang.agentProposalBodies.installmentAmount, value: majorToCents(change.amount), onChange: (cents) => {
                                const next = [...rows];
                                next[i] = { ...next[i], amount: cents / 100 };
                                setRows(next);
                            } }),
                        React.createElement(DateInput, { allowNull: false, placeholder: lang.agentProposalBodies.fromDate, value: moment(change.date), minDate: moment(draft.startDate).toDate(), filterDate: planDateFilter, onChange: (d) => {
                                const next = [...rows];
                                next[i] = {
                                    ...next[i],
                                    date: snapPlanDate(moment(d)),
                                };
                                setRows(next);
                            } }),
                        React.createElement(IconButton, { onClick: () => setRows(rows.filter((_, j) => j !== i)), title: lang.inputs.remove },
                            React.createElement(X, { size: "16px" }))));
                }),
                React.createElement("div", null,
                    React.createElement(Button, { size: "small", theme: "secondary", onClick: () => {
                            const rows = draft.scheduledAmountChanges ?? [];
                            const lastDate = rows[rows.length - 1]?.date ?? draft.startDate;
                            updateDraft({
                                scheduledAmountChanges: [
                                    ...rows,
                                    {
                                        amount: draft.amount ?? 0,
                                        date: snapPlanDate(moment(lastDate).add(1, 'month')),
                                    },
                                ],
                            });
                        } }, lang.agentProposalBodies.addScheduledChange)))),
            editError && (React.createElement(EditError, null,
                lang.agentProposals.invalidEdits,
                ": ",
                editError)))) : (React.createElement(React.Fragment, null,
            React.createElement(TopRow, null,
                React.createElement(Primary, null, input.method === 'amount' && input.amount != null ? (React.createElement(React.Fragment, null,
                    React.createElement(Money, { amount: majorToCents(input.amount) }),
                    React.createElement(Cadence, null, cadence))) : (React.createElement(React.Fragment, null,
                    React.createElement("span", null,
                        input.duration,
                        " ",
                        lang.agentProposalBodies.installments),
                    React.createElement(Cadence, null, cadence)))),
                onEditsChange != null && (React.createElement(IconButton, { onClick: () => setEditing(true), title: lang.misc.edit },
                    React.createElement(Edit2, { size: "16px" })))),
            React.createElement(Secondary, null,
                lang.agentProposalBodies.startsOn,
                " ",
                formatDate(input.startDate)),
            input.amountUpfront != null && (React.createElement(Detail, null,
                React.createElement(DetailLabel, null,
                    lang.agentProposalBodies.upfront,
                    ":"),
                React.createElement(Money, { amount: Math.round(input.amountUpfront * 100) }),
                ' ',
                lang.agentProposalBodies.upfrontInline,
                ' ',
                formatDate(input.dateUpfront ?? input.startDate))),
            input.method === 'amount' &&
                input.scheduledAmountChanges &&
                input.scheduledAmountChanges.length > 0 && (React.createElement(Detail, null,
                React.createElement(DetailLabel, null,
                    lang.agentProposalBodies.scheduledChanges,
                    ":"),
                React.createElement(ChangeList, null, input.scheduledAmountChanges.map((c, i) => (React.createElement("li", { key: i },
                    React.createElement(Money, { amount: Math.round(c.amount * 100) }),
                    ' ',
                    React.createElement(DetailLabel, null, lang.agentProposalBodies.fromDate.toLowerCase()),
                    formatDate(c.date))))))))),
        preview.status === 'loading' && React.createElement(StatPlaceholder, null, "\u2026"),
        showValidationErrors && preview.status === 'error' && (React.createElement(PreviewError, null,
            React.createElement(Errors, { errors: [preview.message] }))),
        preview.status === 'ready' && preview.entries.length > 0 && (React.createElement(PlanSummary, { summary: summarizeEntries(preview.entries), lang: lang, method: effective.method }))));
};
const EditFields = styled.div `
  display: flex;
  flex-direction: column;
  gap: 10px;
`;
const Field = styled.div `
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
`;
const UpfrontRow = styled.div `
  display: flex;
  gap: 8px;
`;
const TopRow = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
`;
const EditActions = styled.div `
  display: flex;
  justify-content: flex-end;
`;
const ChangeRow = styled.div `
  display: flex;
  gap: 8px;
  align-items: center;
  margin: 4px 0;
`;
const PreviewError = styled.div `
  margin-top: 12px;
`;
const PlanSummary = ({ summary, lang, method }) => {
    const firstCell = method === 'amount' ? (React.createElement(React.Fragment, null,
        React.createElement(StatLabel, null, lang.agentProposalBodies.installments),
        React.createElement(StatValue, null, summary.entryCount))) : (React.createElement(React.Fragment, null,
        React.createElement(StatLabel, null, lang.agentProposalBodies.summaryPerInstallment),
        React.createElement(StatValue, null, summary.perInstallmentCents != null ? (React.createElement(Money, { amount: summary.perInstallmentCents })) : ('—'))));
    return (React.createElement(StatRow, null,
        React.createElement(StatCell, null, firstCell),
        React.createElement(StatCell, null,
            React.createElement(StatLabel, null, lang.agentProposalBodies.summaryEnds),
            React.createElement(StatValue, null, summary.endDate != null ? formatDate(summary.endDate) : '—')),
        React.createElement(StatCell, null,
            React.createElement(StatLabel, null, lang.agentProposalBodies.summaryTotal),
            React.createElement(StatValue, null,
                React.createElement(Money, { amount: summary.totalCents })))));
};
export default CreatePaymentPlanBody;
//# sourceMappingURL=CreatePaymentPlanBody.js.map